import React, {useEffect, useState, useRef, useMemo} from 'react';
import Table from 'react-bootstrap/Table';
import { RotateCcw, Bluetooth, BluetoothOff, Pencil, PencilOff, Plus, Settings, Trophy, PlayCircle, StopCircle, ZoomIn, ZoomOut } from 'lucide-react';
import { ZStackController } from './zigbee/zigbee.js';

import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import DialogConfirm from './components/DialogConfirm';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./App.css";

import { LS_START_LIST, sendKey } from './constants';
import { LocaleContext, makeT, getLocale } from './i18n';
import { lookupBrandLogo, lookupFlagUrl } from './utils';
import StartOrderIndexItem from './components/StartOrderIndexItem';
import SortableItem from './components/SortableItem';
import AddParticipantModal from './components/AddParticipantModal';
import EditParticipantModal from './components/EditParticipantModal';
import ConfigModal from './components/ConfigModal';
import LoadRaceFromAnubeModal from './components/LoadRaceFromAnubeModal.jsx';
import ToastContainer from './components/ToastContainer';

async function fetchWithRetry(url, retries = 3, delayMs = 1000) {
  for (let attempt = 0; ; attempt++) {
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    } catch (e) {
      if (attempt >= retries - 1) throw e;
      await new Promise(res => setTimeout(res, delayMs * (attempt + 1)));
    }
  }
}

export default function App() {
  const keyboardActivated = useRef(false);
  const [participants, setParticipants] = useState([]);
  const [dragSelected, setDragSelected] = useState(-1);
  const [zbConnected, setZbConnected] = useState(false);
  const [zbConnecting, setZbConnecting] = useState(false);
  const toastRef = useRef(null);
  const [locale, setLocale] = useState(getLocale);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showTime, setShowTime] = useState(true);
  const [showCodriver, setShowCodriver] = useState(true);
  const [showTeam, setShowTeam] = useState(true);
  const [showAllParticipants, setShowAllParticipants] = useState(true);
  const [eventName, setEventName] = useState(() => makeT(getLocale())('appTitle'));
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showRaceConfigModal, setShowRaceConfigModal] = useState(false);
  const [raceConfig, setRaceConfig] = useState({ raceNumber: '382', itineraryId: 1207, specialId: 7934, useFiaRules: true,  });
  const [mode, setMode] = useState('show'); // 'show' | 'edit' | 'prologue' | 'start'
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [brandsList, setBrandsList] = useState([]);
  const [sessionFinished, setSessionFinished] = useState(false);
  const zbController = useRef(null);
  const participantsRef = useRef([]);
  const pendingActivateRef = useRef(null);
  const pendingNextRef = useRef(null);
  const nextTimerRef = useRef(null);
  const dragHasMovedRef = useRef(false);
  const pendingKeyboardDragRef = useRef(false);
  const [zoom, setZoom] = useState(1.0);

  const dataFetch = async (cfg = raceConfig) => {
    const [data, dataClassif, brandsLogos] = await Promise.all([
      fetchWithRetry(`https://rest3.anube.es/rallyrest/timing/api/participants/${cfg.raceNumber}.json`)
        .catch( (e)=>{console.log(e)} ),
      fetchWithRetry(`https://rest3.anube.es/rallyrest/timing/api/classification/${cfg.raceNumber}.json?itinerary_id=${cfg.itineraryId}&special_id=${cfg.specialId}`)
        .catch( (e)=>{console.log(e)} ),
      fetchWithRetry('https://cdn.anubesport.com/timing/vehicles-brands-logos-v1.json')
        .catch( (e)=>{console.log(e)} ),
    ]);

    const participantList = data.event.data;
    const classifList = dataClassif.event.data.special;
    const first = classifList[0];
    const maxTime = cfg.useFiaRules?(first.total*1.1):Number.MAX_SAFE_INTEGER;

    const pList = classifList.map((c, i) => {
      const pObj = participantList.find(p => p.id === c.participant_id);
      return {
        ...pObj,
        visible: false,
        initialPos: i,
        completed: false,
        choose: true,
        brandLogo: lookupBrandLogo(pObj.vehicle, brandsLogos),
        pilotFlag: lookupFlagUrl(pObj.pilot_nat),
        copilotFlag: lookupFlagUrl(pObj.copilot_nat),
        total: c.total,
        position: c.position,
        time: c.time,
        penalization: c.penalization,
        priorityPilot: (cfg.priorityIdents || '').split(',').map(s => s.trim()).includes(String(pObj.number ?? pObj.ident ?? '')),
      };
    });

    const limit = cfg.useFiaRules?10:(cfg.maxParticipants ?? 10);
    let cnt = 0;
    const filteredList = pList.filter(c => {
      const add = c.priorityPilot || (cnt < limit ? c.total <= maxTime : false);
      if (cnt >= limit) c.choose = false;
      if (add) cnt++;
      return add;
    }).reverse();
    // Default: all visible in view mode
    setParticipants(filteredList.map(p => ({ ...p, visible: true })));
  }

  useEffect(() => {
    fetchWithRetry('https://cdn.anubesport.com/timing/vehicles-brands-logos-v1.json')
      .then(d => { console.log('[brands] loaded', d?.length, 'entries, sample:', d?.[0]); setBrandsList(Array.isArray(d) ? d : []); })
      .catch(err => console.error('[brands] fetch failed:', err));
  }, []);

  useEffect(()=>{
    const localData = localStorage.getItem(LS_START_LIST);
    if(localData) { 
        setParticipants( JSON.parse(localData) );
    }
    },[]);

  useEffect(() => {
    const allAppTitles = ['en', 'pt', 'es', 'fr'].map(l => makeT(l)('appTitle'));
    setEventName(prev => allAppTitles.includes(prev) ? makeT(locale)('appTitle') : prev);
  }, [locale]);

  useEffect(()=>{
    // Save data
    if( participants.length > 0 )
      localStorage.setItem(LS_START_LIST, JSON.stringify(participants));
    participantsRef.current = participants;

    // Focus the newly visible participant so keyboard drag (Space) or mouse drag
    // can be used freely. If pendingKeyboardDragRef is set (session just started),
    // auto-dispatch Space to immediately begin keyboard drag.
    if (pendingActivateRef.current !== null) {
      const id = pendingActivateRef.current;
      pendingActivateRef.current = null;
      const el = document.querySelector(`tr[data-id='${id}']`);
      if (el) {
        el.focus();
        if (pendingKeyboardDragRef.current) {
          pendingKeyboardDragRef.current = false;
          el.dispatchEvent(new KeyboardEvent('keydown', { key: ' ', code: 'Space', bubbles: true }));
        }
      }
    }
  },[participants]);

  // @dnd-kit calculates drag deltas in viewport pixels, but CSS zoom scales
  // the container, so we divide the transform by zoom to keep the element
  // aligned under the pointer. Keyboard dragging doesn't need this correction.
  function zoomModifier({ transform }) {
    if (keyboardActivated.current) return transform;
    return { ...transform, x: transform.x / zoom, y: transform.y / zoom };
  }

  // All measured rects must also be divided by zoom so they are in the same
  // CSS-layout coordinate space as the modifier-adjusted delta, ensuring that
  // collision detection matches the visual drag position.
  const measuring = useMemo(() => {
    function measure(element) {
      const r = element.getBoundingClientRect();
      return {
        width: r.width / zoom, height: r.height / zoom,
        top: r.top / zoom, left: r.left / zoom,
        right: r.right / zoom, bottom: r.bottom / zoom,
      };
    }
    return {
      draggable: { measure },
      droppable: { strategy: MeasuringStrategy.Always, measure },
    };
  }, [zoom]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 8px of movement before starting a drag so normal clicks still work
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      onActivation: () => { keyboardActivated.current = true; },
    })
  );

  const handleDragStart = ({ active }) => {
    dragHasMovedRef.current = false;
    if (keyboardActivated.current) setDragSelected(active.id);
  };

  const handleDragMove = () => {
    dragHasMovedRef.current = true;
  };

  const handleDragCancel = () => {
    keyboardActivated.current = false;
    dragHasMovedRef.current = false;
    if (dragSelected !== -1) setDragSelected(-1);
  };

  function handleDragEnd(event) {
    const {active, over} = event;
    const wasKeyboard = keyboardActivated.current;
    keyboardActivated.current = false;
    dragHasMovedRef.current = false;
    if (dragSelected !== -1) setDragSelected(-1);
    if (!over) return;
    const p = participants.find(e => e.id === active.id);
    if (mode === 'edit') {
      // In edit mode: just reorder, no completion logic, no next-participant activation
      if (active.id !== over.id) {
        setParticipants((current) => {
          const oldIndex = current.findIndex( (e)=>e.id===active.id );
          const newIndex = current.findIndex( (e)=>e.id===over.id );
          const moved = arrayMove(current, oldIndex, newIndex);
          // Keep initialPos in sync so the pick-order list updates live
          return moved.map((p, i) => ({ ...p, initialPos: moved.length - 1 - i }));
        });
      }
    } else if (mode === 'start') {
      setParticipants((current) => {
        const oldIndex = current.findIndex( (e)=>e.id===active.id );
        const newIndex = current.findIndex( (e)=>e.id===over.id );
        const moved = arrayMove(current, oldIndex, newIndex);
        moved[newIndex] = { ...moved[newIndex], completed: wasKeyboard ? true : p.completed };
        if (wasKeyboard) {
          const pickOrder = [...moved].sort((a, b) => b.initialPos - a.initialPos);
          const placedIdx = pickOrder.findIndex(e => e.id === moved[newIndex].id);
          const next = pickOrder[placedIdx + 1];
          if (next) {
            pendingNextRef.current = next.id;
            pendingKeyboardDragRef.current = true;
          } else {
            setSessionFinished(true);
          }
        }
        return moved;
      });
      clearTimeout(nextTimerRef.current);
      nextTimerRef.current = setTimeout(() => {
        const nextId = pendingNextRef.current;
        if (nextId === null) return;
        pendingNextRef.current = null;
        pendingActivateRef.current = nextId;
        setSessionFinished(false);
        setParticipants((current) => {
          const idx = current.findIndex((e) => e.id === nextId);
          if (idx === -1) return current;
          const updated = [...current];
          updated[idx] = { ...updated[idx], visible: true, fadingIn: true };
          return updated;
        });
      }, 1000);
    }
  }

  const connectZigbee = async () => {
    if (zbController.current) {
      await zbController.current.close();
      zbController.current = null;
      setZbConnected(false);
      return;
    }
    const zb = new ZStackController();
    zb.addEventListener('log', (e) =>
      console.log(`[zigbee] ${e.detail.level}: ${e.detail.message}`)
    );
    zb.addEventListener('message', (e) => {
      const { srcEP } = e.detail;
      console.log('[zigbee] message', e.detail);
      if (srcEP === 1) sendKey('ArrowUp');
      else if (srcEP === 2) sendKey('ArrowDown');
      else if (srcEP === 3) sendKey('Enter');
    });
    // Register before open() to avoid missing early disconnect event
    zb.addEventListener('disconnected', () => {
      zbController.current = null;
      setZbConnected(false);
    });

//              this._emit('coordinator-status', { status: 'ready' });

    zb.addEventListener('coordinator-status', (event)=>{
      console.log(`[zigbee] status: ${event.detail.status}`)
      if( event.detail.status === "ready" ) {
        setZbConnected(true);
        toastRef.current?.showZbSuccess();
      }
    });
    zb.addEventListener('permit-join-status', (e) => {
      toastRef.current?.updateZbPjRemaining(e.detail.remaining);
    });
    zb.addEventListener('device-unknown', async () => {
      toastRef.current?.showZbUnknownDevice();
      // Restart permit-join timer for pairing
      if (zbController.current) {
        try {
          await zbController.current.permitJoin(true, 300);
        } catch (e) {
          console.error('Failed to restart permit-join:', e);
        }
      }
    });
    zb.addEventListener('device-joined', () => {
      toastRef.current?.hideZbUnknownDevice();
      toastRef.current?.showZbDevicePaired();
    });
    try {
      setZbConnecting(true);
      toastRef.current?.clearZbError();
      await zb.open(115200);
      zbController.current = zb;
    } catch (err) {
      console.error('Zigbee connect failed:', err);
      const msg = err?.message || '';
      const isInUse = /already open|in use|access denied|busy|failed to open serial port/i.test(msg);
      toastRef.current?.showZbError(isInUse ? t('zigbeePortInUse') : (msg || t('zigbeeConnectError')));
      // Close the partially-initialised controller so the read loop is aborted
      // and the serial port is released; prevents dangling unhandled rejections.
      try { await zb.close(); } catch (_) {}
    } finally {
      setZbConnecting(false);
    }
  };
  
  function addNewParticipant(p) {
    setParticipants(prev => [...prev, p]);
  }

  function deleteParticipant(id) {
    setParticipants(prev => prev.filter(e => e.id !== id));
  }

  function openEditParticipant(id) {
    const p = participants.find(e => e.id === id);
    if (p) setEditingParticipant(p);
  }

  function saveEditedParticipant(updated) {
    setParticipants(prev => prev.map(e => e.id === updated.id ? updated : e));
    setEditingParticipant(null);
  }

  function activateParticipant(id) {
    sendKey('Escape'); // cancel any active keyboard drag before starting a new one
    keyboardActivated.current = false;
    pendingKeyboardDragRef.current = true;
    pendingActivateRef.current = id;
    setParticipants(prev => prev.map(e => e.id === id ? { ...e, visible: true, fadingIn: true } : e));
  }

  function hideParticipant(id) {
    setDragSelected(-1);
    setParticipants(prev => prev.map(e => e.id === id ? { ...e, visible: false, completed: false, fadingIn: false } : e));
  }

  function changeMode(newMode) {
    if (newMode === 'show') {
      setDragSelected(-1);
      if (mode === 'edit') {
        setParticipants(prev => prev.map((p, i) => ({
          ...p, initialPos: prev.length - 1 - i, visible: true, completed: false,
        })));
      } else {
        setParticipants(prev => prev.map(p => ({ ...p, visible: true, fadingIn: false })));
      }
    }
    setMode(newMode);
  }

  function startSession() {
    clearTimeout(nextTimerRef.current);
    setSessionFinished(false);
    setMode('start');
    pendingKeyboardDragRef.current = true;
    setParticipants(prev => {
      if (prev.length === 0) return prev;
      const sorted = [...prev].sort((a, b) => b.initialPos - a.initialPos);
      const firstChoosable = sorted.find(p => p.choose !== false);
      const nextId = firstChoosable ? firstChoosable.id : sorted[0].id;
      nextTimerRef.current = setTimeout(() => {
        pendingActivateRef.current = nextId;
        setParticipants((current) => {
          const idx = current.findIndex((e) => e.id === nextId);
          if (idx === -1) return current;
          const updated = [...current];
          updated[idx] = { ...updated[idx], visible: true, fadingIn: true };
          return updated;
        });
      }, 1000);
      return sorted.map((p) => ({ ...p, visible: p.choose === false, fadingIn: false, completed: false }));
    });
    toastRef.current?.showSessionStarted();
  }

  function continueSession() {
    clearTimeout(nextTimerRef.current);
    pendingKeyboardDragRef.current = true;
    setParticipants(prev => {
      // Only already-completed (and non-choosable) participants stay visible
      const restored = prev.map(p => ({ ...p, visible: p.completed || p.choose === false, fadingIn: false }));
      // Find the next participant to activate (first non-completed choosable in current order)
      const next = restored.find(p => !p.completed && p.choose !== false);
      if (next) {
        nextTimerRef.current = setTimeout(() => {
          pendingActivateRef.current = next.id;
          setParticipants(current => {
            const idx = current.findIndex(e => e.id === next.id);
            if (idx === -1) return current;
            const updated = [...current];
            updated[idx] = { ...updated[idx], visible: true, fadingIn: true };
            return updated;
          });
        }, 500);
      }
      return restored;
    });
    setMode('start');
  }

  function stopSession() {
    sendKey('Escape');
    clearTimeout(nextTimerRef.current);
    pendingNextRef.current = null;
    changeMode('show');
  }

  const displayParticipants = mode === 'prologue'
    ? participants.slice().sort((a, b) => a.initialPos - b.initialPos)
    : participants;

  const effectiveShowTime = showTime && mode !== 'start';
  const t = makeT(locale);

  return (
    <LocaleContext.Provider value={locale}>
    <DndContext 
      sensors={mode === 'show' || mode === 'prologue' ? [] : sensors}
      modifiers={[zoomModifier]}
      measuring={measuring}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragCancel={handleDragCancel}
    >
      <div className='main' style={{ zoom }}>
      <center>
        <SortableContext 
          items={displayParticipants}
          strategy={verticalListSortingStrategy}
        >
          <table className='table-container'>
            <tbody>
            <tr>
              <td align='center'>
                {/* <img className='logo' src='img/Logo_Baja_TT_2025.png'></img> */}
                <h4 style={{}}>{eventName}</h4>
                {mode === 'prologue' ? (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5em',
                    background: 'rgba(255,215,0,0.12)',
                    borderRadius: '40px', padding: '0.4em 1.4em 0.4em 1em',
                    marginBottom: '1.2em',
                    border: '1.5px solid #e6b800',
                    boxShadow: '0 2px 8px rgba(200,160,0,0.18)',
                  }}>
                    <Trophy size={18} color='#c8940a' style={{ flexShrink: 0 }} />
                    <span style={{
                      fontFamily: 'Verdana, sans-serif', fontWeight: 'bold',
                      fontSize: '0.95em', letterSpacing: '0.18em',
                      color: '#9a6f00'
                    }}>{t('prologueResults')}</span>
                  </div>
                ) : (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5em',
                    background: 'rgba(0,170,85,0.1)',
                    borderRadius: '40px', padding: '0.4em 1.4em 0.4em 1em',
                    marginBottom: '1.2em',
                    border: '1.5px solid #00aa55',
                    boxShadow: '0 2px 8px rgba(0,170,85,0.18)',
                  }}>
                    <PlayCircle size={18} color='#007a3d' style={{ flexShrink: 0 }} />
                    <span style={{
                      fontFamily: 'Verdana, sans-serif', fontWeight: 'bold',
                      fontSize: '0.95em', letterSpacing: '0.18em',
                      color: '#005c2e',
                    }}>{t('startPositionSelection')}</span>
                  </div>
                )}
              </td>
            </tr>
            {mode !== 'prologue' && (
              <tr>
                <td align='center' className={"style-frontal-td"}>
                    <span>
                      <span style={{
                        fontSize: '0.65em', fontFamily: 'Verdana, sans-serif',
                        letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase',
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {t('pickOrder')}
                        </span>
                        {participants.slice().sort((a, b) => a.initialPos - b.initialPos).reverse().map(p => (
                          <StartOrderIndexItem key={p.id} id={p.id} ident={p.ident} choose={p.choose} completed={p.completed} mode={mode}
                            activateParticipant={activateParticipant} hide={hideParticipant} />
                        ))}
                      </span>
                </td>
              </tr>
            )}
            <tr>
              <td align='center' >
                <div style={{ display: 'flow', alignItems: 'flex-start', whiteSpace: 'nowrap', gap: 0 }}>
                <Table style={{display:'inline', width:'100%', verticalAlign:'top', marginBottom: 0, borderSpacing: 0}}>          
                    <thead>
                      <tr>
                        <td className='style-position-header'>POS</td>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        displayParticipants.map( (o,i) =>{
                          return (
                            <tr key={'idx-'+i}>
                              <td className='text-center' key={o.id} style={{height:'50px', verticalAlign:'middle'}}>
                                  <div className='style-position'>{i+1}</div>
                              </td>
                            </tr>
                          )
                        })
                      }
                    </tbody>
                </Table>
                <Table style={{width:'', display:'inline', verticalAlign:'top', tableLayout:'fixed', marginBottom: 0}}>
                  <thead>
                  <tr>
                    <td className='text-center' xs={1}></td>
                    <td className='text-center' xs={1}>#</td>
                    {participants.some(p => p.brandLogo) && <td className='text-center' xs={1}></td>}
                    <td className='text-center' xs={3}>{showTeam ? 'TEAM' : ''}</td>
                    <td className='text-center' xs={4}>DRIVER</td>
                    {showCodriver && <td className='text-center' xs={4}>CO-DRIVER</td>}
                    {effectiveShowTime && <td className='text-center' xs={2}>TIME</td>}
                    <td className='text-center' xs={1}></td>
                  </tr>
                  </thead>
                  <tbody>
                    {
                      displayParticipants.map( (o,i) =>{
                        return (<SortableItem key={o.id} id={o.id} index={i} participant={o} showAllParticipants={mode === 'prologue' ? true : showAllParticipants} selected={mode === 'prologue' ? -1 : dragSelected} showTime={effectiveShowTime} showCodriver={showCodriver} showTeam={showTeam} className="pilot-list-row "
                          fadingIn={!!o.fadingIn}
                          onFadeEnd={id => setParticipants(prev => prev.map(e => e.id === id ? { ...e, fadingIn: false } : e))}
                          editMode={mode === 'edit'}
                          draggable={mode === 'edit' || mode === 'start'}
                          onDelete={deleteParticipant}
                          onEdit={openEditParticipant}
                        />)
                      })
                    }

                  </tbody>
                </Table>
                </div>
                {mode === 'start' && sessionFinished && (
                  <div style={{
                    marginTop: '1.5em',
                    fontSize: '1.15em',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    background: 'rgba(0,170,85,0.15)',
                    color: '#00884a',
                    borderRadius: '10px',
                    padding: '1em 2em',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    border: '1.5px solid rgba(0,170,85,0.35)',
                    boxShadow: '0 2px 12px rgba(0,170,85,0.10)',
                  }}>
                    {t('startSelectionFinished')}
                  </div>
                )}
              </td>
            </tr>
            {mode === 'edit' && (
              <tr>
                <td>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '60em', margin: '0.4em auto 0' }}>
                    <button
                      onClick={() => setShowRaceConfigModal(true)}
                      title='Load data from Anube'
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: '#cc2222', border: 'none', borderRadius: '6px',
                        color: 'white', padding: '0.35em 0.9em', cursor: 'pointer',
                        fontFamily: 'Verdana', fontSize: '0.85em',
                      }}
                    >
                      <img alt='logo' src='img/logo-anube.svg' style={{ width: '14px', height: '14px', filter: 'brightness(0) invert(1)' }} /> {t('loadFromAnube')}
                    </button>
                    <button
                      onClick={() => setShowAddModal(true)}
                      title='Add participant'
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: '#00aa55', border: 'none', borderRadius: '6px',
                        color: 'white', padding: '0.35em 0.9em', cursor: 'pointer',
                        fontFamily: 'Verdana', fontSize: '0.85em',
                      }}
                    >
                      <Plus size={15} /> {t('addParticipant')}
                    </button>
                  </div>
                </td>
              </tr>
            )}
            </tbody>
           </table>
           
        </SortableContext>
      </center>
      </div>
      
      {/* <OnScreenRemote reset={()=>setShowResetDialog(true)}/> */}
      <div className='floating-top-left'>
        <div onClick={() =>{sendKey('Escape'); setShowConfigModal(true)}} title={t('configuration')}>
          <Settings color='#888888' />
        </div>
        <div onClick={zbConnecting ? undefined : connectZigbee}
          title={zbConnecting ? t('connectingZigbee') : zbConnected ? t('disconnectZigbee') : t('connectZigbee')}
          style={{ cursor: zbConnecting ? 'default' : 'pointer', animation: zbConnecting ? 'zb-pulse 0.9s ease-in-out infinite' : undefined }}>
          {zbConnected ? <Bluetooth color='#00cc88' /> : <BluetoothOff color={zbConnecting ? '#4488cc' : '#cc4444'} />}
        </div>

        {mode !== 'start' && (
          <div onClick={() => {
            changeMode(mode === 'edit' ? 'show' : 'edit');
            sendKey('Escape');
          }} title={mode === 'edit' ? t('exitEditMode') : t('editParticipants')}>
            {mode === 'edit' ? <PencilOff color='#ffcc00' /> : <Pencil color='#888888' />}
          </div>
        )}
        {mode === 'edit' && (
          <div onClick={() => setShowResetDialog(true)} title={t('reset')}>
            <RotateCcw color='#888888' />
          </div>
        )}
        {mode !== 'start' && (
          <div onClick={() => { sendKey("Escape"); changeMode(mode === 'prologue' ? 'show' : 'prologue'); }}
            title={mode === 'prologue' ? t('switchToStart') : t('switchToPrologue')}>
            <Trophy color={mode === 'prologue' ? '#ffcc00' : '#888888'} />
          </div>
        )}
        {mode !== 'edit' && (
          <div
            onClick={mode === 'start' ? stopSession : () => {
              if (participants.some(p => p.completed)) setShowSessionWarning(true);
              else startSession();
            }}
            title={mode === 'start' ? t('stopSession') : t('startSession')}
            style={{ cursor: 'pointer' }}
          >
            {mode === 'start'
              ? <StopCircle color='#cc4444' />
              : <PlayCircle color={participants.length > 0 ? '#00aa55' : '#888888'} />}
          </div>
        )}
      </div>
      <div className='floating-bottom-left'>
        <div onClick={() => setZoom(z => Math.min(2, parseFloat((z + 0.1).toFixed(1))))} title={t('zoomIn')}>
          <ZoomIn color='#888888' />
        </div>
        <div onClick={() => setZoom(z => Math.max(0.3, parseFloat((z - 0.1).toFixed(1))))} title={t('zoomOut')}>
          <ZoomOut color='#888888' />
        </div>
      </div>
      {mode !== 'edit' && (
        <div className='floating-tl'>
          <div>Powered by</div>
          <img alt='R3 logo' src='img/r3-new-logo.png'></img>
        </div>
      )}

      <ToastContainer ref={toastRef} t={t} />
        {showAddModal && <AddParticipantModal brandsList={brandsList} onAdd={addNewParticipant} showTime={showTime} onClose={() => setShowAddModal(false)} participants={participants} />}
        {showConfigModal && <ConfigModal
          config={{ showTime, showCodriver, showTeam, showAllParticipants, eventName, locale }}
          onChange={(key, val) => {
            if (key === 'showTime') setShowTime(val);
            else if (key === 'showCodriver') setShowCodriver(val);
            else if (key === 'showTeam') setShowTeam(val);
            else if (key === 'showAllParticipants') setShowAllParticipants(val);
            else if (key === 'eventName') setEventName(val);
            else if (key === 'locale') setLocale(val);
          }}
          onClose={() => setShowConfigModal(false)}
        />}
        {showRaceConfigModal && <LoadRaceFromAnubeModal
          initialConfig={raceConfig}
          onApply={cfg => { 
            setRaceConfig(cfg); 
            if (cfg.eventName) setEventName(cfg.eventName); 
            dataFetch(cfg).catch(err => {
              console.error('dataFetch failed:', err);
              toastRef.current?.showZbError(err?.message || t('loadError'));
            });
          }}
          onClose={() => setShowRaceConfigModal(false)}
        />}
        {editingParticipant && <EditParticipantModal participant={editingParticipant} brandsList={brandsList} onSave={saveEditedParticipant} showTime={showTime} onClose={() => setEditingParticipant(null)} participants={participants} />}
        <DialogConfirm show={showResetDialog}
          danger
          title={t('resetTitle')}
          body={t('resetBody')}
          confirmLabel={t('resetConfirm')}
          handleCancel={() => setShowResetDialog(false)}
          handleConfirm={() => { setShowResetDialog(false); localStorage.clear(); setParticipants([]);}}>
        </DialogConfirm>
        <DialogConfirm show={showSessionWarning}
          title={t('startNewSession')}
          body={t('sessionWarningBody')}
          handleCancel={() => setShowSessionWarning(false)}
          handleConfirm={() => { setShowSessionWarning(false); startSession(); }}
          confirmLabel={t('newSession')}
          extraAction={{ label: t('continueSession'), variant: 'success', onClick: () => { setShowSessionWarning(false); continueSession(); } }}>
        </DialogConfirm>
    </DndContext>
    </LocaleContext.Provider>
  );
}